import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@utils/cn';

import { Button, Icon, Text } from '@ui-component-library';

import { AlertDialog } from '@components/PropertySelector/components/AlertModal';

import type { AlertDialogContent, Product } from '../../Checkout.types';
import { useCheckout } from '../../context/CheckoutContext';

import { SelectProductDialog } from './SelectProductDialog';
import { SelectProductSummaryRow } from './SelectProductSummaryRow';

export function SelectProduct({ showSummary = false }) {
  const {
    selectedProducts,
    removeSelectedProduct,
    openSelectProductDialog,
    setOpenSelectProductDialog,
    addSelectedProduct,
    startedAsAnonymous,
    setSelectedProducts,
    config,
    userProducts
  } = useCheckout();

  const [pendingSelection, setPendingSelection] = useState<Product[] | null>(null);
  const [selectProductLoading, setSelectProductLoading] = useState(false);
  const [alertQueue, setAlertQueue] = useState<AlertDialogContent[]>([]);

  const selectedProductsRef = useRef(selectedProducts);
  selectedProductsRef.current = selectedProducts;

  useEffect(() => {
    if (!openSelectProductDialog) {
      setPendingSelection(null);
      return;
    }
    if (config.confirmProductSelection) {
      setPendingSelection([...selectedProductsRef.current]);
    }
  }, [openSelectProductDialog, config.confirmProductSelection]);

  const handleOpenDialog = () => {
    setOpenSelectProductDialog(true);
  };

  const getButtonText = () => {
    if (startedAsAnonymous) {
      return selectedProducts.length > 0 ? 'Sök fler' : 'Sök';
    }
    return selectedProducts.length > 0 ? 'Ändra' : 'Lägg till';
  };

  const buttonText = getButtonText();

  const dialogSelectionSource = config.confirmProductSelection
    ? (pendingSelection ?? selectedProducts)
    : selectedProducts;

  const handleToggleInDialog = useCallback(
    (product: Product) => {
      if (config.confirmProductSelection) {
        setPendingSelection((prev) => {
          const currentPendingSelection = prev ?? [...selectedProducts];
          const isProductSelected = currentPendingSelection.some((p) => p.id === product.id);
          if (isProductSelected) {
            return currentPendingSelection.filter((p) => p.id !== product.id);
          }
          return [...currentPendingSelection, product];
        });
        return;
      }
      if (!selectedProducts.some((p) => p.id === product.id)) {
        addSelectedProduct(product);
      } else {
        removeSelectedProduct(product);
      }
    },
    [config.confirmProductSelection, selectedProducts, addSelectedProduct, removeSelectedProduct]
  );

  const confirmLabel =
    config.selectProductConfirmLabel ?? (config.confirmProductSelection ? 'Lägg till' : 'Klar');

  const handlePrimaryAction = useCallback(async () => {
    if (config.confirmProductSelection) {
      const pending = pendingSelection ?? [...selectedProducts];
      if (pending.length === 0) {
        config.handleSelectedProductsOnClose?.();
        setOpenSelectProductDialog(false);
        return;
      }
      setSelectProductLoading(true);
      try {
        const result = await config.confirmProductSelection({
          previousSelected: selectedProducts,
          pendingSelected: pending,
          userProducts
        });
        setSelectedProducts(result.nextSelected);
        const alerts = result.alerts;
        if (alerts?.length) {
          setAlertQueue((queue) => [...queue, ...alerts]);
        }
      } finally {
        setSelectProductLoading(false);
      }
    }
    config.handleSelectedProductsOnClose?.();
    setOpenSelectProductDialog(false);
  }, [
    config.confirmProductSelection,
    pendingSelection,
    selectedProducts,
    userProducts,
    setSelectedProducts,
    config.handleSelectedProductsOnClose,
    setOpenSelectProductDialog
  ]);
  return (
    <>
      <div className="flex flex-row items-start justify-start gap-4">
        <Icon
          name="MapPinSimple"
          size="large"
          spacing="medium"
          className="fill-brown-700 bg-stone-200"
        />
        <div
          className={cn(
            'flex w-full flex-row justify-between gap-1',
            selectedProducts.length > 0 ? 'items-start' : 'items-center'
          )}
        >
          <div className="flex flex-col gap-1">
            <Text variant="text-sm/leading-normal/medium">
              {selectedProducts.length > 0
                ? `Valda fastigheter (${selectedProducts.length})`
                : config.selectProductLabel}
            </Text>
            {selectedProducts.length > 0 &&
              config.selectedProductSummationText !== undefined &&
              config.getSelectedProductSummationTotal && (
                <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                  {config.getSelectedProductSummationTotal(selectedProducts)}{' '}
                  {config.selectedProductSummationText}
                </Text>
              )}
            {showSummary &&
              selectedProducts.map((product) => (
                <SelectProductSummaryRow key={product.id} product={product} />
              ))}
          </div>
          <Button variant="secondary" size="small" onClick={handleOpenDialog}>
            {buttonText}
          </Button>
        </div>
      </div>
      {!showSummary && selectedProducts.length > 0 && (
        <div className="h-[0.5px] w-full border border-solid border-stone-500/17" />
      )}
      {!showSummary &&
        selectedProducts.map((product) => (
          <div key={product.id}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-row items-center justify-between gap-1">
                <div className="flex flex-col gap-1">
                  <SelectProductSummaryRow product={product} />
                  <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                    {product.description}
                  </Text>
                </div>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => removeSelectedProduct(product)}
                >
                  Ta bort
                </Button>
              </div>
            </div>
          </div>
        ))}

      {selectedProducts.some((product) => product.hasBadData) && (
        <div className="mt-4 flex flex-row gap-2 rounded-md bg-stone-100 p-3">
          <Icon name="InfoFilled" size="medium" className="fill-pebble-500" />
          <div className="flex flex-col gap-2">
            <Text variant="text-xs/leading-normal/normal" className="text-pebble-500">
              Några av de fastigheter du har valt ligger i områden där den officiella skogsdatan är
              av <b>generellt lägre kvalitet</b>.
            </Text>
            <Text variant="text-xs/leading-normal/medium" className="text-pebble-500">
              Fastigheter med begränsad datakvalitet:
            </Text>
            <ul className="list-disc pl-4">
              {selectedProducts
                .filter((product) => product.hasBadData)
                .map((product) => (
                  <Text
                    as="li"
                    key={product.id}
                    variant="text-xs/leading-normal/normal"
                    className="text-pebble-500 capitalize"
                  >
                    {product.name}
                  </Text>
                ))}
            </ul>
            {config.badDataAdditionalInfo ? (
              <Text variant="text-xs/leading-normal/normal" className="text-pebble-500 mt-2">
                {config.badDataAdditionalInfo}
              </Text>
            ) : null}
          </div>
        </div>
      )}

      {createPortal(
        <>
          <SelectProductDialog
            selectedProducts={dialogSelectionSource}
            onToggleProduct={handleToggleInDialog}
            confirmLabel={confirmLabel}
            loading={selectProductLoading}
            onPrimaryAction={handlePrimaryAction}
            primaryRequiresNonEmptySelection={Boolean(config.confirmProductSelection)}
          />
          {alertQueue[0] ? (
            <AlertDialog
              {...alertQueue[0]}
              isOpen
              onClose={() => setAlertQueue((queue) => queue.slice(1))}
            />
          ) : null}
        </>,
        document.body
      )}
    </>
  );
}
